import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "d-none d-md-flex justify-content-center" }
const _hoisted_2 = {
  class: "stepper",
  "aria-hidden": "true"
}
const _hoisted_3 = ["tabindex", "onClick", "onKeyup"]
const _hoisted_4 = ["href"]
const _hoisted_5 = { tabindex: "-1" }
const _hoisted_6 = { class: "d-block d-md-none" }
const _hoisted_7 = { class: "breadcrumb-mobile" }
const _hoisted_8 = { class: "breadcrumb-text" }
const _hoisted_9 = { class: "breadcrumb-number" }
const _hoisted_10 = { class: "breadcrumb-number" }

import { ref, computed, onMounted } from "vue";
import { isImpersonating } from "@/guiide/ts/utilities";
import { useStore } from "vuex";
import { workflow } from "@/guiide/ts/workflow/workflow";
import { WorkflowStep } from "@/guiide/ts/workflow/workflowStep";
import View from "@/guiide/ts/view";
import RetirementAge from "./RetirementAge.vue";
import CompositeIcon from "./CompositeIcon.vue";
import CompositeIconInfo from "@/guiide/ts/components/compositeIconInfo";

enum MenuItemState {
  Enabled = "",
  Active = "active",
  Disabled = "disabled"
}
export default /*@__PURE__*/_defineComponent({
  __name: 'WorkflowNavigation',
  props: {
    workflowStage: { type: String, required: true }
},
  setup(__props) {

const props = __props;

const store = useStore();
const workflowSteps = ref<Array<WorkflowStep>>(null);

;

onMounted(async () => {
  workflowSteps.value = await workflow.getSteps();
});

const showRetirementAge = computed<boolean>(() => store.state.retiree != null && store.state.configuration.workflowShowsRetirementAge);
const impersonating = computed<boolean>(() => isImpersonating());
const stepCount = computed<number>(() => workflowSteps.value?.length ?? 0);
const showIcons = computed<boolean>(() => store.state.configuration?.workflow?.showIcons ?? false);

function getStageClass(stage: number, workflowStep: WorkflowStep): string {
      if (impersonating.value && store.state.transactionOngoing) {
        return MenuItemState.Disabled;
      }

      const currentStage = getCurrentStage();
      const isNextStep = stage == (currentStage + 1);
      const allowImmediateAccessToNextPage = workflowStep?.allowDirectAccessFromPreviousPage ?? false;

      if (isNextStep && allowImmediateAccessToNextPage) {
        const activeOrEnabled = stage == currentStage ? MenuItemState.Active : MenuItemState.Enabled;
        return activeOrEnabled;
      }

      if (stage < currentStage) {
        return MenuItemState.Enabled;
      } else if (stage == currentStage) {
        return MenuItemState.Active;
      } else {
        const enabledState = impersonating.value ? MenuItemState.Enabled : MenuItemState.Disabled;
        const isAccessible = isStageAccessible(stage, workflowStep);

        if (isAccessible) {
          return enabledState;
        }

        return `${enabledState} inaccessible`;
      }
}

function isStageAccessible(stage: number, workflowStep: WorkflowStep): boolean {
      if (impersonating.value && store.state.transactionOngoing) {
        return false;
      }

      const currentStage = getCurrentStage();
      const isNextStep = stage == (currentStage + 1);
      const allowImmediateAccessToNextPage = workflowStep?.allowDirectAccessFromPreviousPage ?? false;

      if (isNextStep && allowImmediateAccessToNextPage) {
        return stage == currentStage ? false : true;
      }

      if (stage < currentStage) {
        return true;
      } else if (stage == currentStage) {
        return false;
      } else if (stage > currentStage) {
        return impersonating.value ? true : false;
      }

      return true;
}

function getCurrentStage(): number {
  if (!workflowSteps.value) {
    return 1;
  }

  let currentStep = workflowSteps.value.filter(ws => ws.text == props.workflowStage)[0];

  if (currentStep == undefined && props.workflowStage == "Finish") {
    currentStep = workflowSteps.value.filter(ws => ws.text == "Review")[0];
  }

  return workflowSteps.value.indexOf(currentStep) + 1;
}

function getNavIcon(view: View): Array<CompositeIconInfo> {
  const size = "2.2rem";
  const top = "-0.5rem";
  const left = "-1.12rem";

  switch (view) {
    case View.You:
      return [ { icon: "bi-person", size, top, left } ];
    case View.Life:
      return [ { icon: "bi-clipboard2-pulse", size, top, left } ];
    case View.Requirements:
      return [
        { icon: "bi-calendar", size, top, left },
        { icon: "bi-currency-pound", size: "1.4rem", top: "-0.0rem", left: "-0.74rem" }
      ];
    case View.DBOptions:
      return [
        { icon: "bi-bank", size, top, left },
        { icon: "bi-gear", size: "0.9rem", top: "-1.0rem", left: "0.6rem" }
      ];
    case View.Haves:
      return [ { icon: "bi-bank", size, top, left } ];
    case View.Design:
      return [ { icon: "bi-palette", size, top, left } ];
    case View.Test:
      return [ { icon: "bi-eyedropper", size, top, left } ];
    case View.Finish:
      return [ { icon: "bi-eye", size, top, left } ];
    case View.Secure:
      return [ { icon: "bi-lightbulb", size, top, left } ];
    default:
      return [ { icon: "bi-cloud", size, top, left } ];
  }
}

function changeLocation(event: any, newLocation: string, isStageAccessible: boolean) {
  event.preventDefault();

  if (newLocation.length === 0 || !isStageAccessible) {
    return;
  }

  workflow.goToWorkflowStep(newLocation);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (showRetirementAge.value)
      ? (_openBlock(), _createBlock(RetirementAge, { key: 0 }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("ol", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workflowSteps.value, (workflowStep, i) => {
          return (_openBlock(), _createElementBlock("li", {
            key: i,
            class: _normalizeClass(getStageClass(i + 1, workflowStep)),
            tabindex: isStageAccessible(i + 1, workflowStep) ? 0 : -1,
            onClick: ($event: any) => (changeLocation($event, workflowStep.resource, isStageAccessible(i + 1, workflowStep))),
            onKeyup: _withKeys(_withModifiers(($event: any) => (changeLocation($event, workflowStep.resource, isStageAccessible(i + 1, workflowStep))), ["prevent"]), ["enter"])
          }, [
            (showIcons.value)
              ? (_openBlock(), _createBlock(CompositeIcon, {
                  key: 0,
                  icons: getNavIcon(workflowStep.view)
                }, null, 8, ["icons"]))
              : (_openBlock(), _createElementBlock("a", {
                  key: 1,
                  class: "stepNumber",
                  tabindex: "-1",
                  href: workflowStep.resource
                }, _toDisplayString(workflowStep.number), 9, _hoisted_4)),
            _createElementVNode("span", _hoisted_5, _toDisplayString(workflowStep.text), 1)
          ], 42, _hoisted_3))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("span", _hoisted_8, _toDisplayString(__props.workflowStage), 1),
        _createElementVNode("span", null, [
          _createElementVNode("span", _hoisted_9, _toDisplayString(getCurrentStage()), 1),
          _cache[0] || (_cache[0] = _createTextVNode(" / ")),
          _createElementVNode("span", _hoisted_10, _toDisplayString(stepCount.value), 1)
        ])
      ])
    ])
  ], 64))
}
}

})